import { css, html, LitElement, PropertyValues } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { sharedStyles } from "../../styles/shared";
import "../../molecules/landing/landing-footer";
import "../../molecules/landing/landing-navbar";
import { ROUTES } from "../../config";
import { addRouteChangedListener, pushState, reloadApp, removeRouteChangedListener } from "../../util/route-util";
import { setIsMenuOpen } from "../../state/app/app-action";
import { state } from "lit/decorators/state.js";
import { Store } from "../../state/store";
import { themeStore } from "../../state/theme/theme-store";
import { Controller } from "../../state/controller";
import { sessionStore } from "../../state/session/session-store";

interface RouteInfo {
	title: string;
	description: string;
}

const ROUTE_INFO_MAP: Record<string, () => RouteInfo | Promise<RouteInfo>> = {
	[ROUTES.features()]: async () => {
			const id = location.pathname.split("/")?.[2];
			const { ALL_FEATURES } = await import("../../data/all-features");
			const match = ALL_FEATURES.flatMap(c => c.items).find(feature => feature.id === id);
			return {
				title: `Ideamap | Features${match != null ? ` | ${match.name}` : ""}`,
				description: match != null ? `Learn more about how ${match.name} can help you when brainstorming.` : `Learn more about how Ideamap can help you when brainstorming.`
			}
	},
	[ROUTES.help()]: async () => {
			const id = location.pathname.split("/")?.[2];
			const { HELP_SECTIONS } = await import("../../data/help-sections");
			const match = HELP_SECTIONS.find(article => article.id === id);
			return {
				title: `Ideamap | Help Center${match != null ? ` | ${match.title}` : ""}`,
				description: match?.text ?? `Welcome to the official help center of Ideamap. We're here to help you learn about Ideamap.`
			}
	},
	[ROUTES.blog()]: async () => {
		const id = location.pathname.split("/")?.[2];
		const {BLOG_ARTICLES} =  await import("../../data/blog-articles");
		const match = BLOG_ARTICLES.find(article => article.id === id);
		return {
			title: match?.title ?? `Ideamap | Blog`,
			description: match?.text ?? "Welcome to the official blog of Ideamap, your visual workspace for brainstorming with your teams and AI."
		}
	},
	[ROUTES.pricing]: () => ({
		title: "Ideamap | Pricing",
		description: "Learn more about Ideamap's pricing plans and how you can get more features."
	}),
	[ROUTES.getStarted]: () => ({
		title: "Ideamap | Get Started",
		description: "Get started with Ideamap for free."
	}),
	[ROUTES.index]: () => ({
		title: "Ideamap | A Better way to Brainstorm with AI",
		description: "Ideamap is a visual workspace for brainstorming where teams collaborate on ideas and use AI to boost their creativity."
	})
}


class LandingPageStore extends Store {
	model$ = {
		colorMode: themeStore.model$.colorMode,
		sessionUser: sessionStore.model$.sessionUser
	};
}

@customElement("bs-landing-page")
class BsLandingPage extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
					display: block;
					background: var(--background);
					color: var(--foreground);
					--title-line-height: 1.1;
				}

				#page {
					min-height: var(--app-height);
				}
			`
		];
	}

	private boundRouteChanged = this.routeChanged.bind(this);
	private boundUpdateDarkmode = this.updateDarkmode.bind(this);
	private controller = new Controller(this, new LandingPageStore(), {
		sessionUser: (user) => {
			// Reload to reset all state and to make sure the tracking is correct and meta tags are correct
			if (user != null) {
				reloadApp(ROUTES.home);
			}
		}
	});

	@state() dark: boolean = false;

	get onlyShowPage() {
		return location.pathname.startsWith(ROUTES.getStarted);
	}

	protected firstUpdated(props: PropertyValues) {
		super.firstUpdated(props);
		addRouteChangedListener(this.boundRouteChanged);
		window.addEventListener("darkmode", this.boundUpdateDarkmode as unknown as any);
		this.updateDocumentTitle();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		removeRouteChangedListener(this.boundRouteChanged);
		window.removeEventListener("darkmode", this.boundUpdateDarkmode as unknown as any);
	}

	routeChanged() {
		this.requestUpdate();
		setIsMenuOpen(false);

		// Scroll to top instantly (for some reason, instant works)
		// @ts-ignore
		window.scrollTo({ top: 0, left: 0, behavior: "instant" });

		this.updateDocumentTitle();
	}

	async updateDocumentTitle() {
		const getInfo = Object.entries(ROUTE_INFO_MAP).find(([route]) => location.pathname.startsWith(route))?.[1]  ?? ROUTE_INFO_MAP[ROUTES.index];
		const {title, description} = await getInfo();
		document.title = title;
		document.querySelector("meta[name='title']")?.setAttribute("content", title);
		document.querySelector("meta[property='og:title']")?.setAttribute("content", title);

		document.querySelector("meta[name='description']")?.setAttribute("content", description);
		document.querySelector("meta[property='og:description']")?.setAttribute("content", description);
	}

	renderPage() {
		const pathname = location.pathname;

		if (pathname.startsWith(ROUTES.pricing)) {
			import("./landing-pricing-page");
			return html`<bs-landing-pricing-page></bs-landing-pricing-page>`;
		}

		if (pathname.startsWith(ROUTES.help())) {
			import("./landing-help-page");
			return html`<bs-landing-help-page></bs-landing-help-page>`;
		}

		if (pathname.startsWith(ROUTES.blog())) {
			import("./landing-blog-page");
			return html`<bs-landing-blog-page></bs-landing-blog-page>`;
		}

		if (pathname.startsWith(ROUTES.getStarted)) {
			import("./landing-get-started-page");
			return html` <bs-landing-get-started-page></bs-landing-get-started-page> `;
		}

		if (pathname.startsWith(ROUTES.features())) {
			import("./landing-features-page");
			return html` <bs-landing-features-page></bs-landing-features-page> `;
		}

		if (location.pathname !== ROUTES.index && this.isConnected) {
			pushState(ROUTES.index, { replace: true, silent: true });
		}

		if (this.controller.model.sessionUser != null) {
			import("./landing-welcome-back-page");
			return html` <bs-landing-welcome-back-page></bs-landing-welcome-back-page> `;
		} else {
			import("./landing-home-page");
			return html`<bs-landing-home-page ?dark="${this.dark}"></bs-landing-home-page>`;
		}
	}

	updateDarkmode(e: CustomEvent<boolean>) {
		this.dark = e.detail;
	}

	render() {
		if (this.onlyShowPage) {
			return html` <main id="page">${this.renderPage()}</main> `;
		}

		const { colorMode } = this.controller.model;
		return html`
			<bs-landing-navbar ?dark="${this.dark || colorMode !== "light"}"></bs-landing-navbar>
			<main id="page">${this.renderPage()}</main>
			<bs-landing-footer></bs-landing-footer>
		`;
	}
}
